import { Typography } from "@mui/material";
import { useGetSubscriptionStatus } from "@queries/subscription/useGetSubscription";
import { GetSubscriptionStatusResponse } from "@shared/apis/sidekick/generated";
import {
  ILocalNotification,
  ILocalNotificationKeys,
  useLocalNotificationStore,
} from "@store/local-notifications.store";
import { useUIStore } from "@store/ui.store";
import {
  differenceInMilliseconds,
  differenceInMinutes,
  formatDistanceToNow,
} from "date-fns";
import { useCallback } from "react";
import { useNavigate } from "react-router";
import { useBypassedValue } from "./useBypassedValue";

type Params = {
  isAuthenticated: boolean;
};

export const useSubscriptionLogic = (params: Params) => {
  const { isAuthenticated } = params;
  const notifications = useLocalNotificationStore(
    (state) => state.notifications
  );
  const updateNotification = useLocalNotificationStore(
    (state) => state.updateNotification
  );
  const confirmDialog = useUIStore((state) => state.confirmDialog);
  const navigate = useNavigate();
  const { getBypassedDateValue } = useBypassedValue({
    queryParam: "trial_end_date",
  });

  const handleTrialReminderNotifications = useCallback(
    async (data: GetSubscriptionStatusResponse) => {
      if (!data.details) return;

      const hasValidPaymentMethod =
        data.details.paymentMethod &&
        (data.details.paymentMethodStatus === "Pending" ||
          data.details.paymentMethodStatus === "Valid");

      if (hasValidPaymentMethod) return;

      data.details.trialEndDate = getBypassedDateValue(
        data.details.trialEndDate
      );

      const {
        trialEndDate,
        paymentStatus,
        // estimatedPaymentMethodRequiredDate,
      } = data.details;

      // DISABLED: while we are not using credits
      // if (
      //   estimatedPaymentMethodRequiredDate &&
      //   isFuture(estimatedPaymentMethodRequiredDate)
      // )
      //   return;

      if (!trialEndDate || paymentStatus !== "Trialing") return;

      const ONE_DAY_MS = 86_400_000;
      const remainingTrialTimeMs = differenceInMilliseconds(
        trialEndDate,
        new Date()
      );

      const handleTrialEndNotification = async () => {
        const { lastNotified } = notifications["free-trial-ended"].value;

        if (
          lastNotified &&
          differenceInMinutes(new Date(), new Date(lastNotified)) < 60
        ) {
          return;
        }

        const res = await confirmDialog({
          title: "Your free trial has ended",
          content: (
            <div>
              <Typography mt={1} mb={1}>
                Update your payment method to continue enjoying our services
                without interruption.
              </Typography>
            </div>
          ),
          confirmButtonText: "Update now",
          cancelButtonText: "Close",
        });

        updateNotification("free-trial-ended", {
          read: false,
          lastNotified: new Date(),
        });

        if (res) {
          navigate("/account/subscription");
        }
      };

      if (remainingTrialTimeMs < 0) {
        handleTrialEndNotification();
        return;
      }

      Object.values(notifications).forEach(
        async (notification: ILocalNotification) => {
          const { reminderThresholdInDays } = notification.config;
          if (!reminderThresholdInDays) return;

          const remainingTimeInDays = Math.round(
            remainingTrialTimeMs / ONE_DAY_MS
          );

          const isReminderDue = remainingTimeInDays <= reminderThresholdInDays;
          const { read } = notification.value;

          if (!isReminderDue || read) return;

          const confirmed = await confirmDialog({
            title: `Your free trial ends ${formatDistanceToNow(trialEndDate, {
              addSuffix: true,
            })}`,
            content: (
              <div>
                <Typography mt={1} mb={1}>
                  Update your payment method now to continue enjoying all the
                  benefits of Sidekick without any interruptions.
                </Typography>
              </div>
            ),
            confirmButtonText: "Update now",
            cancelButtonText: "Remind me later",
            maxWidth: "sm",
            fullWidth: false,
          });

          if (confirmed) {
            navigate("/account/subscription");
          }

          Object.entries(notifications).forEach(([key, n]) => {
            const threshold = n.config.reminderThresholdInDays;
            if (threshold && remainingTimeInDays <= threshold) {
              updateNotification(key as ILocalNotificationKeys, {
                ...n.value,
                read: true,
                lastNotified: new Date(),
              });
            }
          });

          return;
        }
      );
    },
    [
      confirmDialog,
      getBypassedDateValue,
      navigate,
      notifications,
      updateNotification,
    ]
  );

  const { data } = useGetSubscriptionStatus({
    enabled: isAuthenticated,
    onSuccess: async (data) => {
      handleTrialReminderNotifications(data);
    },
  });

  return {
    userSubscription: data ?? null,
  };
};
